import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle, Sparkles, Droplets, Shield, Zap, ArrowRight, Sun, Wrench, HelpCircle } from "lucide-react";
import heroCeramic from "@/assets/hero-ceramic.jpg";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const CeramicCoatings = () => {
  return (
    <div className="min-h-screen">
      <Helmet>
        <title>Ceramic Coating Las Vegas | XPEL Fusion Plus Specialist</title>
        <meta name="description" content="Las Vegas XPEL Fusion Plus ceramic coating specialists. Superior gloss, hydrophobic protection & UV defense. 1, 4 & 8-year warranty packages. Free consultation." />
        <link rel="canonical" href="https://battlebornppf.com/services/ceramic-coatings" />
        <link rel="preload" as="image" href={heroCeramic} />
      </Helmet>
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src={heroCeramic} 
            alt="XPEL Fusion Plus ceramic coating application on luxury vehicle at Battle Born PPF Las Vegas" 
            className="w-full h-full object-cover"
            width="1920"
            height="1080"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/50" />
        </div>
        
        <div className="relative z-10 container mx-auto px-4">
          <div className="max-w-3xl">
            <Sparkles className="w-16 h-16 text-accent mb-6" />
            <h1 className="text-5xl md:text-6xl font-heading font-bold mb-6 uppercase">
              XPEL FUSION PLUS™ Ceramic Coating
            </h1>
            <p className="text-xl text-muted-foreground">
              Advanced nano-ceramic technology for unmatched gloss, protection, and durability
            </p>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center uppercase">
              Why XPEL FUSION PLUS™?
            </h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-16">
              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Superior Gloss</h3>
                  <p className="text-muted-foreground">
                    Enhances paint depth and creates a stunning mirror-like finish that lasts for years
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Hydrophobic Properties</h3>
                  <p className="text-muted-foreground">
                    Water and contaminants bead off instantly, making maintenance effortless
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Chemical Resistance</h3>
                  <p className="text-muted-foreground">
                    Protects against acid rain, bird droppings, tree sap, and harsh chemicals
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">UV Protection</h3>
                  <p className="text-muted-foreground">
                    Prevents paint oxidation and fading from harmful UV rays
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Easier Cleaning</h3>
                  <p className="text-muted-foreground">
                    Contaminants wash away easily, reducing wash time and effort
                  </p>
                </div>
              </div>

              <div className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-xl font-heading font-semibold mb-2">Long-Lasting Protection</h3>
                  <p className="text-muted-foreground">
                    Professional-grade coatings last up to 8 years with proper maintenance
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* What is Ceramic Coating? */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-8 text-center">
              What is Ceramic Coating?
            </h2>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
              <p>
                Ceramic coating is a liquid polymer infused with silicon dioxide (SiO₂) nanoparticles that chemically bonds to your vehicle's factory clear coat. Unlike traditional wax or sealant that sits on top of the paint and wears away within weeks, ceramic coating creates a permanent or semi-permanent molecular bond with the surface. This forms an incredibly hard, transparent layer of protection that becomes part of the paint itself — it cannot be washed off or broken down by detergents.
              </p>
              <p>
                The result is a surface with exceptional hydrophobic properties: water, mud, and contaminants bead up and roll off rather than clinging to the paint. This dramatically reduces the effort required to keep your vehicle clean and minimizes the risk of water spots, mineral deposits, and chemical etching. The coating also enhances optical clarity and depth of color, giving your paint a deep, wet-looking gloss that surpasses even a freshly polished finish.
              </p>
              <p>
                At Battle Born Protection Films, we exclusively apply <strong className="text-foreground">XPEL FUSION PLUS™</strong>, a professional-grade nano-ceramic coating engineered for maximum durability and performance. Unlike consumer-grade products sold online, XPEL FUSION PLUS™ requires certified application in a controlled environment, ensuring consistent coverage, proper bonding, and results that last for years — not months.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Video Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              See XPEL FUSION PLUS™ In Action
            </h2>
            <p className="text-center text-muted-foreground mb-8">
              Watch how our ceramic coating delivers unmatched protection and shine
            </p>
            <div className="relative aspect-video rounded-lg overflow-hidden border border-border">
              <iframe
                src="https://www.youtube.com/embed/0kgESqYU2zY"
                title="XPEL Fusion Plus Ceramic Coating"
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      </section>

      {/* Packages Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center uppercase">
              XPEL FUSION PLUS™ Packages
            </h2>
            
            <div className="grid md:grid-cols-3 gap-8 mb-16">
              {/* 1-Year Package */}
              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Shield className="w-16 h-16 text-accent mx-auto mb-4" />
                    <h3 className="text-2xl font-heading font-bold mb-2 uppercase">1-Year Warranty</h3>
                    <p className="text-accent font-semibold text-lg">Enhanced Protection</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Enhanced gloss finish</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Hydrophobic water beading</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Easier cleaning & maintenance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">UV & chemical protection</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* 4-Year Package */}
              <Card className="bg-card border-accent shadow-[var(--shadow-gold-glow)]">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Shield className="w-16 h-16 text-accent mx-auto mb-4" />
                    <h3 className="text-2xl font-heading font-bold mb-2 uppercase">4-Year Warranty</h3>
                    <p className="text-accent font-semibold text-lg">Standard Protection</p>
                    <span className="inline-block mt-2 px-3 py-1 bg-accent text-accent-foreground text-xs font-heading uppercase rounded-sm">
                      Most Popular
                    </span>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Superior durability</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Advanced chemical resistance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Enhanced scratch resistance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Deep, lasting gloss</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              {/* 8-Year Package */}
              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    <Shield className="w-16 h-16 text-accent mx-auto mb-4" />
                    <h3 className="text-2xl font-heading font-bold mb-2 uppercase">8-Year Warranty</h3>
                    <p className="text-accent font-semibold text-lg">Ultimate Protection</p>
                  </div>
                  <ul className="space-y-3">
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Thickest ceramic layer</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Maximum gloss enhancement</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Premium self-healing properties</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                      <span className="text-muted-foreground">Industry-leading durability</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Add-Ons Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-12 text-center uppercase">
              Complete Your Protection
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Sparkles className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Wheels & Calipers</h3>
                  <p className="text-muted-foreground">
                    Brake dust resistance and easier cleaning for wheels and calipers
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Droplets className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Glass</h3>
                  <p className="text-muted-foreground">
                    Rain repellent coating for improved visibility in wet conditions
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Shield className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Trim & Plastic</h3>
                  <p className="text-muted-foreground">
                    Fade prevention and restoration for exterior trim and plastics
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-6">
                  <Zap className="w-10 h-10 text-accent mb-3" />
                  <h3 className="text-xl font-heading font-bold mb-2">Interior Upholstery</h3>
                  <p className="text-muted-foreground">
                    Stain resistance and protection for leather and fabric surfaces
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Paint Correction Section */}
      <section className="py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              Paint Correction
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Before ceramic coating application, paint correction removes imperfections to ensure a flawless finish
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold mb-4 uppercase">1-Step Correction</h3>
                  <p className="text-accent font-semibold mb-4">Removes 50-70% of Swirls</p>
                  <p className="text-muted-foreground mb-6">
                    Single-stage polishing process that eliminates light to moderate surface imperfections, 
                    enhancing gloss and preparing the surface for ceramic coating.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Light swirl removal</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Enhanced gloss finish</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Cost-effective solution</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>

              <Card className="bg-card border-silver">
                <CardContent className="p-8">
                  <h3 className="text-2xl font-heading font-bold mb-4 uppercase">2-Step Correction</h3>
                  <p className="text-accent font-semibold mb-4">Removes 85-95% of Defects</p>
                  <p className="text-muted-foreground mb-6">
                    Advanced two-stage process combining compounding and polishing to achieve a 
                    near-perfect mirror finish, eliminating deep scratches and severe imperfections.
                  </p>
                  <ul className="space-y-2">
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Deep scratch removal</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Mirror-like finish</span>
                    </li>
                    <li className="flex items-center gap-2">
                      <CheckCircle className="w-5 h-5 text-accent flex-shrink-0" />
                      <span className="text-muted-foreground">Show-quality results</span>
                    </li>
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-6 text-center uppercase">
              The Battle Born Process
            </h2>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Our meticulous 4-step process ensures flawless ceramic coating application
            </p>
            
            <div className="grid md:grid-cols-4 gap-6">
              {/* Step 1 */}
              <div className="relative">
                <Card className="bg-card border-silver h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">01</span>
                      <Droplets className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Decontamination</h3>
                    <p className="text-sm text-muted-foreground">
                      Chemical wash and clay bar treatment to remove all surface contaminants
                    </p>
                  </CardContent>
                </Card>
                <ArrowRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 w-8 h-8 text-accent z-10" />
              </div>

              {/* Step 2 */}
              <div className="relative">
                <Card className="bg-card border-silver h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">02</span>
                      <CheckCircle className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Inspection</h3>
                    <p className="text-sm text-muted-foreground">
                      Paint thickness measurement and defect mapping with precision tools
                    </p>
                  </CardContent>
                </Card>
                <ArrowRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 w-8 h-8 text-accent z-10" />
              </div>

              {/* Step 3 */}
              <div className="relative">
                <Card className="bg-card border-silver h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">03</span>
                      <Sparkles className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Restoration</h3>
                    <p className="text-sm text-muted-foreground">
                      Paint correction and polishing to achieve a flawless surface
                    </p>
                  </CardContent>
                </Card>
                <ArrowRight className="hidden md:block absolute -right-6 top-1/2 -translate-y-1/2 w-8 h-8 text-accent z-10" />
              </div>

              {/* Step 4 */}
              <div>
                <Card className="bg-card border-accent shadow-[var(--shadow-gold-glow)] h-full">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-4">
                      <span className="text-5xl font-heading font-bold text-accent">04</span>
                      <Shield className="w-10 h-10 text-accent" />
                    </div>
                    <h3 className="text-lg font-heading font-bold mb-2 uppercase">Application</h3>
                    <p className="text-sm text-muted-foreground">
                      Precision layering of XPEL FUSION PLUS™ ceramic coating
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Professional vs DIY */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Wrench className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Professional vs. DIY Ceramic Coating
              </h2>
            </div>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
              <p>
                Consumer ceramic coating kits are widely available, but the results they deliver are dramatically different from professional application. DIY products typically contain lower SiO₂ concentrations and thinner formulations that wear off within months. Professional-grade XPEL FUSION PLUS™ uses a higher concentration of nano-ceramic particles and requires controlled curing conditions that simply aren't achievable in a home garage.
              </p>
              <p>
                Perhaps the most important difference is surface preparation. Before any ceramic coating is applied, the paint must be thoroughly decontaminated and corrected. Swirl marks, scratches, water spots, and oxidation that are sealed under a ceramic coating become permanent — the coating locks in whatever condition the paint is in. Our certified technicians perform professional paint correction before every coating application, ensuring a flawless foundation.
              </p>
              <p>
                Professional application also comes with XPEL's manufacturer warranty, covering the coating against premature failure. DIY applications carry no such guarantee, and improper application — uneven coverage, high spots, or streaking — can actually make your paint look worse than before. When you invest in ceramic coating, the professional difference is worth every penny.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Las Vegas Desert Protection */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <Sun className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Ceramic Coating for the Las Vegas Desert
              </h2>
              <p className="text-xl text-muted-foreground">
                Purpose-built protection for the harshest conditions
              </p>
            </div>
            <div className="space-y-6 text-muted-foreground text-lg leading-relaxed">
              <p>
                Las Vegas presents unique challenges that make ceramic coating not just a luxury, but a necessity for paint preservation. With UV index readings regularly exceeding 10 during summer months, unprotected clear coat oxidizes and fades at an accelerated rate. XPEL FUSION PLUS™ provides a UV-resistant barrier that prevents this degradation, keeping your paint vibrant and glossy despite relentless sun exposure.
              </p>
              <p>
                Dust storms and blowing sand are a constant reality in the Mojave Desert. Fine particulates settle on every surface and, when combined with morning dew or sprinkler overspray, create an abrasive paste that can micro-scratch unprotected paint. Ceramic coating's hardened surface resists these micro-abrasions, and its hydrophobic properties cause dust and water to bead off rather than bond to the surface. Hard water spots from lawn sprinklers — a common headache for Las Vegas homeowners — wipe away effortlessly from a coated vehicle.
              </p>
              <p>
                Brake dust, another persistent issue for performance and luxury vehicles, etches into uncoated wheels and paint within days in the desert heat. Our ceramic coating packages include wheel and caliper coating options that prevent brake dust from bonding, keeping your wheels clean between washes and protecting expensive finishes from permanent staining.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance Guide */}
      <section className="py-20 bg-muted/20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-heading font-bold mb-8 text-center">
              Caring for Your Ceramic-Coated Vehicle
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-4 text-accent">Do</h3>
                  <ul className="space-y-3">
                    {["Wash every 1–2 weeks with pH-neutral car soap", "Use the two-bucket wash method to prevent swirls", "Dry with a clean microfiber towel or air blower", "Apply XPEL-approved ceramic boost spray every 3–6 months", "Schedule annual professional inspections"].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <CheckCircle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
              <Card className="bg-card border-border">
                <CardContent className="p-6">
                  <h3 className="text-xl font-heading font-bold mb-4 text-destructive">Avoid</h3>
                  <ul className="space-y-3">
                    {["Automatic car washes with abrasive brushes", "Alkaline or acidic cleaners not designed for coatings", "Wax or sealant products (they interfere with the coating)", "Washing in direct sunlight on hot panels", "Letting bird droppings or tree sap sit for extended periods"].map((item) => (
                      <li key={item} className="flex items-start gap-3">
                        <span className="w-5 h-5 text-destructive flex-shrink-0 mt-0.5 text-center font-bold">✕</span>
                        <span className="text-muted-foreground">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Ceramic Coating FAQ */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <HelpCircle className="w-16 h-16 text-accent mx-auto mb-6" />
              <h2 className="text-4xl font-heading font-bold mb-4">
                Frequently Asked Questions
              </h2>
            </div>
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="how-long" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  How long does ceramic coating last?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  Depending on the package, XPEL FUSION PLUS™ ceramic coating lasts 1 to 8+ years. Our 8-Year package provides the thickest ceramic layer and maximum durability. Longevity depends on maintenance habits, environmental exposure, and how frequently the vehicle is washed properly.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="ppf-combo" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  Can ceramic coating be applied over PPF?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  Absolutely — in fact, we recommend it. Applying ceramic coating over PPF enhances the film's hydrophobic properties, makes it easier to clean, and adds an additional layer of UV protection. Many of our clients opt for PPF on high-impact areas with ceramic coating over the entire vehicle for complete protection.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="paint-correction" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  Is paint correction necessary before ceramic coating?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  We strongly recommend it. Ceramic coating bonds permanently to your paint's surface, locking in whatever condition it's currently in. Any swirls, scratches, or water spots present before application will be sealed underneath the coating. Paint correction ensures a flawless canvas for maximum visual impact.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="wash" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  Can I take my car through a car wash?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  We recommend avoiding automatic car washes with abrasive brushes, as they can degrade the coating over time. Touchless car washes are acceptable, but hand washing with pH-neutral soap is ideal. The hydrophobic properties of ceramic coating actually make hand washing faster and easier than an uncoated vehicle.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="new-vs-used" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  Is ceramic coating only for new cars?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  Not at all. Ceramic coating can be applied to any vehicle regardless of age. For older vehicles, we perform paint correction first to remove years of accumulated swirls, scratches, and oxidation, then seal the restored finish with ceramic coating. Many clients are amazed at how a used car can look better than new after correction and coating.
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="how-long-install" className="border border-border rounded-lg px-6 bg-card">
                <AccordionTrigger className="text-lg font-heading font-semibold hover:text-accent">
                  How long does the application process take?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground text-base leading-relaxed">
                  A complete ceramic coating application — including decontamination, paint correction, and coating — typically takes 2–3 days. The coating itself needs 24–48 hours to fully cure in our climate-controlled facility before the vehicle is ready for pickup. We'll provide a specific timeline during your consultation.
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-accent/20 to-accent/5">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl md:text-5xl font-heading font-bold mb-6">
            Enhance Your Vehicle's Beauty
          </h2>
          <p className="text-xl text-muted-foreground mb-8 max-w-2xl mx-auto">
            Discover the perfect ceramic coating package for your vehicle
          </p>
          <Button asChild variant="hero" size="lg">
            <Link to="/contact">Request Free Quote</Link>
          </Button>
        </div>
      </section>

      {/* Schema Markup */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "XPEL FUSION PLUS Ceramic Coating",
          "provider": {
            "@type": "AutomotiveBusiness",
            "name": "Battle Born Protection Films",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "6960 W Russell Rd B",
              "addressLocality": "Las Vegas",
              "addressRegion": "NV",
              "postalCode": "89113",
              "addressCountry": "US"
            }
          },
          "areaServed": {
            "@type": "City",
            "name": "Las Vegas"
          },
          "description": "Professional XPEL FUSION PLUS ceramic coating application providing superior gloss, hydrophobic protection, and long-lasting durability with warranty packages from 1 to 8 years."
        })}
      </script>
    </div>
  );
};

export default CeramicCoatings;
